import { useReducer } from 'react';

import { AuthScreens } from 'globals/constants';

export interface AuthorizationResponse extends MessageResponse {
  authToken: string;
  /** if equals 0, then redirect to lmsAuthUrl */
  hasLmsApiToken: NumericBoolean;
  lmsAuthUrl: string;
}

export interface LinkEmailResponse extends MessageResponse {
  userId: string;
  cluster: string;
}

export interface LinkPasswordResponse extends MessageResponse {
  linkToken: string;
}

export type GetSsoStatusResponse =
  | {
      message: 'waiting' | 'success';
      errorMessage: '';
    }
  | {
      message: 'failed';
      errorMessage: string;
    };

export interface AuthModuleState {
  screen: AuthScreens;
  isResettingPassword: boolean;
  verifyCodeContext: VerifyCodeContext;
  authResponse: AuthorizationResponse | null;
  linkEmailResponse: LinkEmailResponse | null;
  linkPasswordResponse: LinkPasswordResponse | null;
}

const defaultState: AuthModuleState = {
  screen: AuthScreens.IMPORT_DATA,
  isResettingPassword: false,
  verifyCodeContext: 'email',
  authResponse: null,
  linkEmailResponse: null,
  linkPasswordResponse: null,
};

export enum AuthActions {
  SET_SCREEN = 'SET_SCREEN',
  SET_SCREEN_WITH_CONTEXT = 'SET_SCREEN_WITH_CONTEXT',
  GOTO_SET_PASSWORD = 'GOTO_GET_PASSWORD',
  GOTO_LINK_ACADLY_PASSWORD = 'GET_ACADLY_EMAIL',
  GOTO_LINK_VERIFY_NEW_EMAIL = 'GOTO_LINK_VERIFY_NEW_EMAIL',
  AUTHORIZE_ACADLY = 'AUTHORIZE_ACADLY',
  SSO_AUTHENTICATION = 'SSO_AUTHENTICATION',
  SSO_LOGIN = 'SSO-LOGIN',
}

interface SetScreenAction {
  readonly type: AuthActions.SET_SCREEN;
  readonly payload: AuthScreens;
}

interface SsoAuthenticationAction {
  readonly type: AuthActions.SSO_AUTHENTICATION;
  readonly payload: AuthScreens;
}

interface SsoLoginAction {
  readonly type: AuthActions.SSO_LOGIN;
  readonly payload: AuthScreens;
}

interface SetScreenWithContextAction {
  readonly type: AuthActions.SET_SCREEN_WITH_CONTEXT;
  readonly payload: {
    screen: AuthScreens;
    context?: VerifyCodeContext;
  };
}

interface GotoSetPasswordAction {
  readonly type: AuthActions.GOTO_SET_PASSWORD;
  readonly payload?: {
    isResettingPassword: boolean;
  };
}

interface GotoLinkAcadlyPasswordAction {
  readonly type: AuthActions.GOTO_LINK_ACADLY_PASSWORD;
  readonly payload: LinkEmailResponse;
}

interface GotoLinkVerifyNewEmailAction {
  readonly type: AuthActions.GOTO_LINK_VERIFY_NEW_EMAIL;
  readonly payload: LinkPasswordResponse;
}

interface AuthorizeAcadlyAction {
  readonly type: AuthActions.AUTHORIZE_ACADLY;
  readonly payload: AuthorizationResponse;
}

export type AuthAction =
  | SetScreenAction
  | SetScreenWithContextAction
  | GotoSetPasswordAction
  | GotoLinkAcadlyPasswordAction
  | GotoLinkVerifyNewEmailAction
  | AuthorizeAcadlyAction
  | SsoLoginAction
  | SsoAuthenticationAction;

const authStateReducer = (state: AuthModuleState, action: AuthAction): AuthModuleState => {
  switch (action.type) {
    case AuthActions.SET_SCREEN: {
      return {
        ...state,
        screen: action.payload,
      };
    }
    case AuthActions.SET_SCREEN_WITH_CONTEXT: {
      return {
        ...state,
        screen: action.payload.screen,
        verifyCodeContext: action.payload.context || state.verifyCodeContext,
      };
    }
    case AuthActions.GOTO_SET_PASSWORD: {
      return {
        ...state,
        screen: AuthScreens.SET_PASSWORD,
        isResettingPassword:
          action.payload === undefined ? state.isResettingPassword : action.payload.isResettingPassword,
      };
    }
    case AuthActions.GOTO_LINK_ACADLY_PASSWORD: {
      return {
        ...state,
        screen: AuthScreens.GET_PASSWORD,
        verifyCodeContext: 'link',
        linkEmailResponse: action.payload,
      };
    }
    case AuthActions.GOTO_LINK_VERIFY_NEW_EMAIL: {
      return {
        ...state,
        screen: AuthScreens.VERIFY_CODE,
        verifyCodeContext: 'link',
        linkPasswordResponse: action.payload,
      };
    }
    case AuthActions.AUTHORIZE_ACADLY: {
      return {
        ...state,
        authResponse: action.payload,
        screen: AuthScreens.LMS_AUTHORIZATION,
      };
    }
    case AuthActions.SSO_AUTHENTICATION: {
      return {
        ...state,
        screen: action.payload,
      };
    }
    case AuthActions.SSO_LOGIN: {
      return {
        ...state,
        screen: action.payload,
      };
    }
    default:
      throw new Error('Invalid auth action');
  }
};

const useAuthStateManager = () => {
  const [state, dispatch] = useReducer(authStateReducer, defaultState);
  return { state, dispatch };
};

export default useAuthStateManager;
